import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/hooks/useAuth";
import { AppLayout } from "@/components/AppLayout";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { usePageTracking } from "@/hooks/usePageTracking";
import Home from "./pages/Home";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import TrialRequests from "./pages/TrialRequests";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";

import MyLearning from "./pages/MyLearning";
import Transcript from "./pages/Transcript";
import CoursePlayer from "./pages/CoursePlayer";
import TakeSurvey from "./pages/TakeSurvey";
import TermsOfUse from "./pages/TermsOfUse";
import MeetingTracker from './pages/MeetingTracker';
import NewTeacherMentorProgram from './pages/NewTeacherMentorProgram';
import MentoringReports from './pages/MentoringReports';
import KPIManagement from './pages/KPIManagement';
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CourseProposals from "./pages/CourseProposals";
import EnhancedCourseCatalog from "./pages/EnhancedCourseCatalog";
import CourseCreationTutorials from "./pages/CourseCreationTutorials";
import ManageUsers from "./pages/ManageUsers";
import CourseManagement from "./pages/CourseManagement";
import CourseBuilder from "./pages/CourseBuilder";
import CourseBuilderEnhanced from "./pages/CourseBuilderEnhanced";
import CourseBuilderIndex from "./pages/CourseBuilderIndex";
import MyCourses from "./pages/MyCourses";
import MyPortfolio from "./pages/MyPortfolio";
import MyGroups from "./pages/MyGroups";
import VideoLibrary from "./pages/VideoLibrary";
import VideoAssignments from "./pages/VideoAssignments";
import PurchaseOrders from "./pages/PurchaseOrders";
import InvoiceManagement from "./pages/InvoiceManagement";
import SharedPortfolio from "./pages/SharedPortfolio";
import RosterCreator from "./pages/RosterCreator";
import ProposalQueue from "./pages/ProposalQueue";
import Badges from "./pages/Badges";
import Surveys from "./pages/Surveys";
import SurveyManagement from "./pages/SurveyManagement";
import CertificateManagement from "./pages/CertificateManagement";
import SISConfiguration from "./pages/SISConfiguration";
import TechRequirements from "./components/admin/TechRequirements";
import APIIntegrations from "./components/admin/APIIntegrations";
import SSOConfiguration from "./pages/SSOConfiguration";
import IntegrationsGuide from "./pages/IntegrationsGuide";
import RFPGuide from "./pages/RFPGuide";
import TylerTechIntegration from "./pages/TylerTechIntegration";
import TechRequirementsStatus from "./components/admin/TechRequirementsStatus";
import QuarterlyLearningDays from "./pages/QuarterlyLearningDays";
import AdminReporting from "./pages/AdminReporting";
import TagManagement from "./pages/TagManagement";
import NBCTCoaching from "./pages/NBCTCoaching";
import DistrictManagement from "./pages/DistrictManagement";
import DistrictUsageAnalytics from "./pages/DistrictUsageAnalytics";
import YearlyWrapUp from "./pages/YearlyWrapUp";
import PuttingItIntoPractice from "./pages/PuttingItIntoPractice";
import AdminWalkthroughAssistant from "./components/practice/AdminWalkthroughAssistant";
import CoachingVideoPortfolioSample from "./components/portfolio/CoachingVideoPortfolioSample";
import GroupDetail from "./pages/GroupDetail";
import PersonalInsights from "./pages/PersonalInsights";
import AdminIntelligencePage from "./pages/AdminIntelligence";
import MentorIntelligencePage from "./pages/MentorIntelligence";
import MessagingPage from "./pages/Messaging";
import IntegrationsPage from "./pages/Integrations";
import NotFound from "./pages/NotFound";
import Impersonation from "./pages/Impersonation";
import AdminChat from "./pages/AdminChat";
import Support from "./pages/Support";
import FAQ from "./pages/FAQ";
import UserProfile from "./pages/UserProfile";
import AccessibilitySettings from "./pages/AccessibilitySettings";

const queryClient = new QueryClient();

// Component to handle page tracking
const AppRoutes = () => {
  usePageTracking(); // Track pageviews on route changes
  
  return (
    <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/home" element={<Home />} />
            <Route path="/trial-requests" element={
              <ProtectedRoute>
                <AppLayout>
                  <TrialRequests />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <AppLayout>
                  <Dashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin-dashboard" element={
              <ProtectedRoute>
                <AppLayout>
                  <AdminDashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/course-catalog" element={
              <ProtectedRoute>
                <AppLayout>
                  <EnhancedCourseCatalog />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/my-learning" element={
              <ProtectedRoute>
                <AppLayout>
                  <MyLearning />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/my-portfolio" element={
              <ProtectedRoute>
                <AppLayout>
                  <MyPortfolio />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/my-groups" element={
              <ProtectedRoute>
                <AppLayout>
                  <MyGroups />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/groups/:groupId" element={
              <ProtectedRoute>
                <AppLayout>
                  <GroupDetail />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/insights" element={
              <ProtectedRoute>
                <AppLayout>
                  <PersonalInsights />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/intelligence" element={
              <ProtectedRoute>
                <AppLayout>
                  <AdminIntelligencePage />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/mentor/intelligence" element={
              <ProtectedRoute>
                <AppLayout>
                  <MentorIntelligencePage />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/messaging" element={
              <ProtectedRoute>
                <AppLayout>
                  <MessagingPage />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/integrations" element={
              <ProtectedRoute>
                <AppLayout>
                  <IntegrationsPage />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/video-library" element={
              <ProtectedRoute>
                <AppLayout>
                  <VideoLibrary />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/video-assignments" element={
              <ProtectedRoute>
                <AppLayout>
                  <VideoAssignments />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/purchase-orders" element={
              <ProtectedRoute>
                <AppLayout>
                  <PurchaseOrders />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/invoice-management" element={
              <ProtectedRoute>
                <AppLayout>
                  <InvoiceManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/portfolio/:shareToken" element={<SharedPortfolio />} />
            <Route path="/transcript" element={
              <ProtectedRoute>
                <AppLayout>
                  <Transcript />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/badges" element={
              <ProtectedRoute>
                <AppLayout>
                  <Badges />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/surveys" element={
              <ProtectedRoute>
                <AppLayout>
                  <Surveys />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/survey-management" element={
              <ProtectedRoute>
                <AppLayout>
                  <SurveyManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/course-player/:courseId" element={
              <ProtectedRoute>
                <AppLayout>
                  <CoursePlayer />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/take-survey" element={
              <ProtectedRoute>
                <AppLayout>
                  <TakeSurvey />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/kpi-management" element={
              <ProtectedRoute>
                <AppLayout>
                  <KPIManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/meeting-tracker" element={
              <ProtectedRoute>
                <AppLayout>
                  <MeetingTracker />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/mentoring-reports" element={
              <ProtectedRoute>
                <AppLayout>
                  <MentoringReports />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/mentor-program" element={
              <ProtectedRoute>
                <AppLayout>
                  <NewTeacherMentorProgram />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/course-proposals" element={
              <ProtectedRoute>
                <AppLayout>
                  <CourseProposals />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/course-tutorials" element={
              <ProtectedRoute>
                <AppLayout>
                  <CourseCreationTutorials />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/terms-of-use" element={
              <ProtectedRoute>
                <AppLayout>
                  <TermsOfUse />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/privacy-policy" element={
              <ProtectedRoute>
                <AppLayout>
                  <PrivacyPolicy />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/manage-users" element={
              <ProtectedRoute>
                <AppLayout>
                  <ManageUsers />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/course-management/:courseId" element={
              <ProtectedRoute>
                <AppLayout>
                  <CourseManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/course-builder" element={
              <ProtectedRoute>
                <AppLayout>
                  <CourseBuilderIndex />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/course-builder-enhanced" element={
              <ProtectedRoute>
                <AppLayout>
                  <CourseBuilderEnhanced />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/course-builder/:courseId" element={
              <ProtectedRoute>
                <AppLayout>
                  <CourseBuilderEnhanced />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/my-courses" element={
              <ProtectedRoute>
                <AppLayout>
                  <MyCourses />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/roster-creator" element={
              <ProtectedRoute>
                <AppLayout>
                  <RosterCreator />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/proposal-queue" element={
              <ProtectedRoute>
                <AppLayout>
                  <ProposalQueue />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/certificate-management" element={
              <ProtectedRoute>
                <AppLayout>
                  <CertificateManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/tech-requirements" element={
              <ProtectedRoute>
                <AppLayout>
                  <TechRequirements />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/api-integrations" element={
              <ProtectedRoute>
                <AppLayout>
                  <APIIntegrations />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/sis-configuration" element={
              <ProtectedRoute>
                <AppLayout>
                  <SISConfiguration />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/sso-configuration" element={
              <ProtectedRoute>
                <AppLayout>
                  <SSOConfiguration />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/integrations-guide" element={
              <ProtectedRoute>
                <AppLayout>
                  <IntegrationsGuide />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/rfp-guide" element={
              <ProtectedRoute>
                <AppLayout>
                  <RFPGuide />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/tyler-tech-integration" element={
              <ProtectedRoute>
                <AppLayout>
                  <TylerTechIntegration />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/tech-requirements-status" element={
              <ProtectedRoute>
                <AppLayout>
                  <TechRequirementsStatus />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/quarterly-learning-days" element={
              <ProtectedRoute>
                <AppLayout>
                  <QuarterlyLearningDays />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/reporting" element={
              <ProtectedRoute>
                <AppLayout>
                  <AdminReporting />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/tag-management" element={
              <ProtectedRoute>
                <AppLayout>
                  <TagManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/nbct-coaching" element={
              <ProtectedRoute>
                <AppLayout>
                  <NBCTCoaching />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/coaching-portfolio-sample" element={
              <ProtectedRoute>
                <AppLayout>
                  <CoachingVideoPortfolioSample />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin-walkthrough" element={
              <ProtectedRoute>
                <AppLayout>
                  <AdminWalkthroughAssistant />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/district-management" element={
              <ProtectedRoute>
                <AppLayout>
                  <DistrictManagement />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/district-usage-analytics" element={
              <ProtectedRoute>
                <AppLayout>
                  <DistrictUsageAnalytics />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/yearly-wrap-up" element={
              <ProtectedRoute>
                <AppLayout>
                  <YearlyWrapUp />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/impersonation" element={
              <ProtectedRoute>
                <AppLayout>
                  <Impersonation />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin-chat" element={
              <ProtectedRoute>
                <AppLayout>
                  <AdminChat />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/support" element={
              <ProtectedRoute>
                <AppLayout>
                  <Support />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/faq" element={
              <ProtectedRoute>
                <AppLayout>
                  <FAQ />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <AppLayout>
                  <UserProfile />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/accessibility-settings" element={
              <ProtectedRoute>
                <AppLayout>
                  <AccessibilitySettings />
                </AppLayout>
              </ProtectedRoute>
            } />
            <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <AppRoutes />
          </TooltipProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
